import { createContext, useContext, useEffect, useRef, useState } from 'react';
import './style.scss';
import ArrowBack from '../../assets/icons/arrowBack.svg';
import { useNavigate } from 'react-router-dom';
import { getBeneficiary } from '../../API/apiService';

const RightSidePaneContext = createContext();

function useOutsideAlerter(ref, inputRef) {
  useEffect(() => {
    function handleClickOutside(event) {
      if (
        ref.current &&
        !ref.current.contains(event.target) &&
        inputRef.current &&
        !inputRef.current.contains(event.target) &&
        inputRef.current.checked
      ) {
        ref.current.click();
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [inputRef, ref]);
}

function RightSidePaneProvider({ children }) {
  const [rightPaneOpened, setRightPaneOpened] = useState(false);
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);
  useOutsideAlerter(wrapperRef, inputRef);

  function show(show) {
    const back = document.getElementById('paneBack');
    const root = document.querySelector(':root');
    if (show) {
      root.style.setProperty('--right-side-pane-display', `block`);
      setTimeout(() => {
        back.checked = false;
      }, 100);
    } else {
      back.checked = true;
      setTimeout(() => {
        root.style.setProperty('--right-side-pane-display', `none`);
        setRightPaneOpened(false);
      }, 600);
    }
  }

  function setDefault() {
    setRightPaneOpened(false);
  }

  const [inputValue, setInputValue] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [beneficiary, setBeneficiary] = useState(null);

  const navigate = useNavigate();

  async function searchBeneficiary() {
    const b = await getBeneficiary(inputValue);
    if (b) {
      setShowCreate(false);
      setBeneficiary(b);
    } else {
      setShowCreate(true);
    }
  }

  async function createBeneficiary() {
    show(false);
    navigate(`/caracterizacion/${inputValue}`);
  }

  function handleInputChange(event) {
    const value = event.target.value;
    if (value) {
      setInputValue(value.toString().trim());
    } else {
      setInputValue(value);
    }
  }

  function handleClearInput() {
    setInputValue('');
    setShowCreate(false);
    setBeneficiary(null);
  }

  useEffect(() => {
    const back = document.getElementById('paneBack');
    if (back) {
      setTimeout(() => {
        back.checked = !rightPaneOpened;
      }, 100);
    }
  }, [rightPaneOpened]);

  return (
    <RightSidePaneContext.Provider
      value={{ rightPaneOpened, show, setDefault }}>
      <input
        type='radio'
        ref={inputRef}
        name='rightPaneRadio'
        onChange={({ target }) => setRightPaneOpened(!target.checked)}
        id='paneBack'
      />
      <div ref={wrapperRef} className='pane'>
        <div
          className='pane__content content'
          onClick={event => event.stopPropagation()}>
          <label
            htmlFor='paneBack'
            className='content__back back'
            onClick={() => show(false)}>
            <img src={ArrowBack} alt='arrow_back' className='back__icon' />
          </label>
          <div className='content__wrapper' id='rightSidePaneWrapper'>
            <div className='search'>
              <form className='search__form form'>
                <label htmlFor='document' className='form__input input'>
                  <span className='material-symbols-rounded input__preffix'>
                    badge
                  </span>
                  <input
                    placeholder='Número de documento'
                    type='text'
                    id='document'
                    name='document'
                    value={inputValue}
                    onChange={handleInputChange}
                  />
                  {inputValue && (
                    <button
                      type='button'
                      className='input__clear clear'
                      onClick={handleClearInput}>
                      <span className='material-symbols-rounded clear__icon'>
                        close
                      </span>
                    </button>
                  )}
                </label>
                <button
                  type='button'
                  onClick={searchBeneficiary}
                  className='form__btn'>
                  <span className='material-symbols-rounded'>search</span>
                </button>
              </form>
              {showCreate && (
                <div className='search__create create'>
                  <div className='create__message message'>
                    <h2 className='message__text'>
                      No existe ningún beneficiario creado.
                    </h2>
                  </div>
                  <button
                    type='button'
                    onClick={createBeneficiary}
                    className='create__btn btn'>
                    <span className='material-symbols-rounded btn__icon'>
                      person_add
                    </span>
                    <span className='btn__text'>Crear Beneficiario</span>
                  </button>
                </div>
              )}
              {beneficiary && (
                <div className='search__res res'>
                  <div className='res__name name'>
                    <h3 className='name__text'>{beneficiary.full_name}</h3>
                  </div>
                  <div className='res__info info'>
                    <h2 className='info__label'>Escuela</h2>
                    <h2 className='info__label'>Grupo</h2>
                    <h2 className='info__label'>Dinamizador</h2>
                    <h2 className='info__label info__label--value'>
                      {beneficiary.school?.code}
                    </h2>
                    <h2 className='info__label info__label--value'>
                      {beneficiary.group?.code}
                    </h2>
                    <h2 className='info__label info__label--value'>
                      {beneficiary.user?.full_name}
                    </h2>
                  </div>
                  <button
                    type='button'
                    onClick={createBeneficiary}
                    className='res__edit edit'>
                    <span className='material-symbols-rounded edit__icon'>
                      edit
                    </span>
                    <span className='edit__text'>Editar</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      {children}
    </RightSidePaneContext.Provider>
  );
}

function useRightSidePane() {
  const context = useContext(RightSidePaneContext);
  if (!context) {
    throw new Error(
      'useRightSidePane must be used within a RightSidePaneProvider'
    );
  }
  return context;
}

export { RightSidePaneProvider, useRightSidePane };
